/**
 * The huddle message's card in a conversation (Slack's huddle post with "Huddle notes"): who was there and for how
 * long, the AI notes (summary, action items, decisions) once written, and the transcript (collapsed). Data from
 * core `Huddles.recapView`. Recordings are replies in the message's thread (regular video files).
 */
import type { HuddleRecap, Member } from "@bundle/contracts";
import { Huddles } from "@bundle/core";
import { useState } from "react";
import { Button, Card, Icon, Spinner, Stack, Text } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";

export type HuddleRecapCardProps = {
  recap: HuddleRecap;
  members: Record<string, Member>;
  testID: string;
};

function Bullets({ items, testID }: { items: string[]; testID: string }) {
  return (
    <Stack gap="1" testID={testID}>
      {items.map((b, i) => (
        <Stack key={i} direction="horizontal" gap="1.5">
          <Text variant="footnote" color="text.secondary">
            •
          </Text>
          <Stack grow>
            <Text variant="footnote" selectable>
              {b}
            </Text>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

export function HuddleRecapCard({ recap, members, testID }: HuddleRecapCardProps) {
  const t = useT();
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const v = Huddles.recapView(recap, members);
  const notes = v.notes;
  return (
    <Card padding="3" gap="2.5" testID={testID}>
      <Stack direction="horizontal" align="center" gap="2">
        <Icon name="huddle" size="icon.sm" color={v.live ? "status.success" : "text.secondary"} />
        <Text variant="footnote" weight="semibold">
          {v.live ? t("huddle.recap.live") : t("huddle.recap.title")}
        </Text>
        <Stack grow>
          <Text variant="footnote" color="text.secondary" lines={1} testID={`${testID}-meta`}>
            {[v.duration === undefined ? undefined : tr(v.duration), v.people.join(", ")].filter((x) => x !== undefined && x !== "").join(" · ")}
          </Text>
        </Stack>
      </Stack>
      {notes === undefined && !v.notesPending ? null : (
        <Stack gap="2" testID={`${testID}-notes`}>
          <Stack direction="horizontal" align="center" gap="1.5">
            <Icon name="sparkles" size="icon.sm" color="accent.text" />
            <Text variant="footnote" weight="semibold">
              {t("huddle.recap.notes")}
            </Text>
          </Stack>
          {notes === undefined ? (
            <Stack direction="horizontal" align="center" gap="1.5" testID={`${testID}-pending`}>
              <Spinner size="icon.sm" accessibilityLabel={t("huddle.recap.notesPending")} />
              <Text variant="footnote" color="text.secondary">
                {t("huddle.recap.notesPending")}
              </Text>
            </Stack>
          ) : (
            <>
              <Text variant="caption" weight="semibold" color="text.secondary">
                {t("huddle.recap.summary")}
              </Text>
              <Bullets items={notes.summary} testID={`${testID}-summary`} />
              {notes.actionItems.length === 0 ? null : (
                <>
                  <Text variant="caption" weight="semibold" color="text.secondary">
                    {t("huddle.recap.actionItems")}
                  </Text>
                  <Bullets items={notes.actionItems} testID={`${testID}-actions`} />
                </>
              )}
              {notes.decisions.length === 0 ? null : (
                <>
                  <Text variant="caption" weight="semibold" color="text.secondary">
                    {t("huddle.recap.decisions")}
                  </Text>
                  <Bullets items={notes.decisions} testID={`${testID}-decisions`} />
                </>
              )}
              <Text variant="caption" color="text.tertiary">
                {t("huddle.recap.aiNotice")}
              </Text>
            </>
          )}
        </Stack>
      )}
      {v.transcript.length === 0 ? null : (
        <Stack gap="2">
          <Stack direction="horizontal">
            <Button
              label={open ? t("huddle.recap.hideTranscript") : t("huddle.recap.showTranscript")}
              icon={open ? "chevronUp" : "chevronDown"}
              size="sm"
              variant="ghost"
              onPress={() => setOpen(!open)}
              testID={`${testID}-transcript-toggle`}
            />
          </Stack>
          {!open ? null : (
            <Stack gap="1.5" testID={`${testID}-transcript`}>
              {v.transcript.map((row, i) => (
                <Stack key={i} gap="0.5">
                  <Stack direction="horizontal" align="center" gap="1.5">
                    <Text variant="caption" weight="semibold">
                      {row.name}
                    </Text>
                    <Text variant="caption" color="text.tertiary">
                      {row.time}
                    </Text>
                  </Stack>
                  <Text variant="footnote" selectable>
                    {row.text}
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Card>
  );
}
